import Layout from 'components/layout';

import React, { type FC, type PropsWithChildren } from 'react';

import { graphql, type HeadFC } from 'gatsby';
import { Box, LucidHead } from '@lucid/components-react';
import type {
  Drupal_FieldNodeNineteenFieldNineteenParagraphs,
  Drupal_NodeNineteenHomepage,
  Drupal_Paragraph,
} from 'generated/drupal-graphql';
import NineteenAccordionComponent from 'components/nineteen/nineteen-accordion/nineteen-accordion';
import NineteenAction from 'components/nineteen/nineteen-action/nineteen-action';
import NineteenArticle from 'components/nineteen/nineteen-article/nineteen-article';
import NineteenCardComponent from 'components/nineteen/nineteen-card-component/nineteen-card-component';
import NineteenCertification from 'components/nineteen/nineteen-certification/nineteen-certification';
import NineteenContact from 'components/nineteen/nineteen-contact/nineteen-contact';
import NineteenFooterCard from 'components/nineteen/nineteen-footer-card/nineteen-footer-card';
import NineteenHeroImage from 'components/nineteen/nineteen-hero-image/nineteen-hero-image';
import NineteenHero from 'components/nineteen/nineteen-hero/nineteen-hero';
import NineteenHighlights from 'components/nineteen/nineteen-highlights/nineteen-highlights';
import NineteenInsights from 'components/nineteen/nineteen-insights/nineteen-insights';
import NineteenOverviewTable from 'components/nineteen/nineteen-overview-table/nineteen-overview-table';
import NineteenPaper from 'components/nineteen/nineteen-paper/nineteen-paper';
import NineteenQuote from 'components/nineteen/nineteen-quote/nineteen-quote';
import NineteenRectangleLinks from 'components/nineteen/nineteen-rectangle-links/nineteen-rectangle-links';
import type { CustomDrupal, Template } from 'types/types';

const Nineteen: FC<PropsWithChildren<Template.PageProps>> = ({ data, pageContext }) => {
  const { nid, shortLangPrefix, pageType, shouldHideSsr } = pageContext;
  const { drupal } = data;
  const node = drupal.nodeById as Drupal_NodeNineteenHomepage;
  const componentMapping: { [key: string]: any } = {
    nineteen_accordion_component: NineteenAccordionComponent,
    nineteen_action: NineteenAction,
    nineteen_article: NineteenArticle,
    nineteen_card_component: NineteenCardComponent,
    nineteen_certification_component: NineteenCertification,
    nineteen_contact_component: NineteenContact,
    nineteen_footer_card: NineteenFooterCard,
    nineteen_hero: NineteenHero,
    nineteen_hero_image: NineteenHeroImage,
    nineteen_highlights: NineteenHighlights,
    nineteen_insights: NineteenInsights,
    nineteen_overview_table: NineteenOverviewTable,
    nineteen_paper: NineteenPaper,
    nineteen_quote: NineteenQuote,
    nineteen_rectangle_link_item: NineteenRectangleLinks,
  };
  const nineteenParagraphs = node.fieldNineteenParagraphs as Drupal_FieldNodeNineteenFieldNineteenParagraphs[];

  return (
    <Layout
      entityTranslations={node.entityTranslations as CustomDrupal.ExtendedDrupalNode[]}
      metatags={node.entityMetatags as Template.Metatag[]}
      nid={nid}
      pageType={pageType}
      shortLangPrefix={shortLangPrefix}
      shouldHideSsr={shouldHideSsr}
    >
      <Box>
        {nineteenParagraphs.map(({ entity }) => {
          const { entityBundle, entityId } = entity as Drupal_Paragraph;
          const NineteenComponent = componentMapping[entityBundle as string];
          return NineteenComponent ? <NineteenComponent key={entityId} entity={entity} /> : null;
        })}
      </Box>
    </Layout>
  );
};

export default Nineteen;

export const Head: HeadFC<Template.PageProps['data'], Template.PageProps['pageContext']> = ({ ...props }) => (
  <LucidHead<Drupal_NodeNineteenHomepage> {...props} />
);

export const query = graphql`
  query ($nid: String!, $LanguageId: Drupal_LanguageId!) {
    drupal {
      nodeById(id: $nid, language: $LanguageId) {
        ... on Drupal_NodeNineteenHomepage {
          ...entityFields
          fieldNineteenParagraphs {
            entity {
              entityId
              entityBundle
              ...nineteenArticle
              ...nineteenAccordionComponent
              ...nineteenCardComponent
              ...nineteenCertificationComponent
              ...nineteenContactComponent
              ...nineteenFooterCard
              ...nineteenHero
              ...nineteenHeroImage
              ...nineteenHighlights
              ...nineteenInsights
              ...nineteenOverviewTable
              ...nineteenPaper
              ...nineteenQuote
              ...nineteenRectangleLinks
            }
          }
        }
      }
    }
  }
`;
